/**
 * billing-view.js
 */

import React from 'react';
import axios from 'axios';
import { useMediaQuery, Container, Box, Typography, Link as MuiLink, Chip, Tooltip } from '@mui/material';
import Alert from '@mui/material/Alert';
import makeStyles from '@mui/styles/makeStyles';
import { Link, useParams, useHistory } from 'react-router-dom';
import { parseISO, format, formatDistance } from 'date-fns';
import { useSnackbar } from 'notistack';
import { Types } from 'utils/constants';
import useUser from 'utils/use-user';
import { UserContext } from 'contexts/user/user-context';
import { AppBarContext } from 'contexts/app-bar/app-bar-context';
import { AppUtilityContext } from 'contexts/app-utility/app-utility-context';
import { LiveTrackingContext } from 'components/tracking/live-tracking/live-tracking-context';
import { AppMessagingContext, APP_SUBSCRIPTION_TYPE } from 'contexts/app-messaging/app-messaging-context';
import EditButton from 'components/edit-button';
import PageTitle from 'components/page-title';
import PageTitleForUser from 'components/page-title-for-user';
import FeatureBox from 'components/feature-box';
import Icon from 'components/icon';
import Button from 'components/button';
import BarButton from 'components/layout/app-bar/bar-button';
import Card from 'components/card';
import IconSection from 'components/icon-section';
import {
  SubscriptionType,
  SubscriptionTypeName,
  PaymentType,
  PaymentTypeName,
  SubscriptionStatus,
} from '../billing-constants';
import BillingEdit from '../edit/billing-edit';


const useStyles = makeStyles(theme => ({
  root: {
    paddingTop: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      paddingTop: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
    '& .MuiFormControl-root': {
      width: 200
    }
  },
  card: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2)
    }
  },
  subCard: {
    display: 'flex',
    flexDirection: 'column',
    '& > .title': {
      justifyContent: 'space-between',
      marginBottom: theme.spacing(1)
    },
  },
  subList: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  subscription: {
    margin: `${theme.spacing(2)} 0`,
    '& .title': {
      '& .MuiTypography-root': {
        fontSize: theme.typography.sizes[4],
      },
      '& .MuiChip-root': {
        marginLeft: theme.spacing(2),
        height: 24,
        fontWeight: 'bold',
        transform: `translateY(-1px)`,
      },
      '& .chip-canceled-overdue': {
        backgroundColor: theme.palette.error.light
      }
    }
  },
  actionButton: {
    marginTop: theme.spacing(2),
    textTransform: 'none',
    minWidth: 110,
  },
  actionButtonLink: {
    '&:hover': {
      textDecoration: 'none',
    }
  },
  linkButton: {
    color: theme.palette.primary.colors[4],
    textDecoration: 'underline',
    cursor: 'pointer'
  },
  editButton: {
    marginLeft: theme.spacing(2),
  },  
  billingPortalCard: {
    '& .portal-link': {
      marginTop: theme.spacing(2),
      textDecoration: 'none',
      color: theme.palette.sys.onPrimary
    },
  },
}));


const ActionButton = (props) => {
  const classes = useStyles();
  return (
    <Button
      {...props}
      variant='contained'
      color='secondary'
      className={`${props.className || ''} ${classes.actionButton}`}
    />
  );
}


const ActionButtonLink = (props) => {
  const classes = useStyles();
  return (
    <ActionButton
      {...props}
      component={MuiLink}
      className={`${props.className || ''} ${classes.actionButtonLink}`}
    />
  );
}


const LinkButton = (props) => {
  const classes = useStyles();
  return (
    <span
      {...props}
      className={classes.linkButton}
    />
  );
}


export default function BillingView(props) {
  const classes = useStyles();
  const { user, setUser } = React.useContext(UserContext);
  const loggedInUserId = user?.id || 0;
  const { setAppBar } = React.useContext(AppBarContext);
  const { alertError } = React.useContext(AppUtilityContext);
  const { isActive: isTrackingActive } = React.useContext(LiveTrackingContext);
  const { subscribe, unsubscribe } = React.useContext(AppMessagingContext);
  const { enqueueSnackbar } = useSnackbar();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  const params = useParams();
  const history = useHistory();
  const userId = params.userId ? parseInt(params.userId) : null;
  const requestedUser = useUser(userId, false);
  const urlParams = new URLSearchParams(window.location.search);
  const [errorParam] = React.useState(urlParams.get('error'));
  const [isLoading, setIsLoading] = React.useState(false);
  const [subscriptionData, setSubscriptionData] = React.useState();
  const [billingUser, setBillingUser] = React.useState();
  const handlerSubscription = subscriptionData?.subscriptions?.find(s => s.role.id === Types.Role.HANDLER);
  const hasHandlerSub = Boolean(handlerSubscription && handlerSubscription?.subscriptionId);
  const hasHandlerRole = Boolean((billingUser || user)?.availableRoles?.find(r => r.id === Types.Role.HANDLER));
  const paidUntilDate = handlerSubscription?.paidToDate ? parseISO(handlerSubscription.paidToDate) : null;
  const billingEditPath = `${userId ? `/${userId}` : ''}/billing/edit`;
  const [title, setTitle] = React.useState();
  const titleBase = 'Billing';
  const {
    isEditDisabled,
    editDisabledReason
  } = (() => {
    let isEditDisabled = false;
    let editDisabledReason = '';

    if (Boolean(subscriptionData?.isBypassed)) {
      isEditDisabled = true;
      editDisabledReason = 'Your subscription is bypassed and cannot be edited';
    } else if (!hasHandlerRole) {
      isEditDisabled = true;
      editDisabledReason = 'Your subscription is free and cannot be edited';
    } else if (handlerSubscription?.status?.id === SubscriptionStatus.CANCELED && handlerSubscription?.balance > 0) {
      isEditDisabled = true;
      editDisabledReason = 'You cannot edit your subscription until you pay your outstanding balance.';
    }

    return {
      isEditDisabled,
      editDisabledReason,
    };
  })();


  // refresh billing data from server
  const updateData = React.useCallback(async () => {
    setIsLoading(true);
    const response = await axios.get('/billing', { params: { userId } });
    if (response.status === 200) {
      setSubscriptionData(response.data.subscription);
      setBillingUser(userId ? response.data.user : null);

      // the user returned here may not be the logged in user.
      // only update the global user object if it is.
      if (response.data.user.id === loggedInUserId) {
        setUser(response.data.user);
      }
    }
    setIsLoading(false);
  }, [userId, setUser, loggedInUserId]);


  const onWindowFocus = React.useCallback(() => {
    updateData();
    window.removeEventListener('focus', onWindowFocus);
  }, [updateData]);


  // Trigger a billing data reload after linking to and returning from a page that might have
  // altered the billing data, e.g. the billing portal.
  const openExternalActionableUrl = (url) => {
    window.removeEventListener('focus', onWindowFocus);
    window.addEventListener('focus', onWindowFocus);
    window.open(url, isMobile ? '_self' : '_blank');
  }


  const openBillingPortal = async () => {
    const response = await axios.get('/billing/stripe-portal', { params: { userId } });
    if (response.status === 200) {
      const url = response.data.portalSessionUrl;
      openExternalActionableUrl(url);
    }
  }


  // init
  React.useEffect(() => {
    const actions = [<BarButton
      onClick={() => updateData()}
      children={<Icon type='refresh' />}
    />];

    document.title = `PACKTRACK - ${titleBase}`;
    const title = <PageTitleForUser title={titleBase} userName={requestedUser?.name || ''} />;
    setTitle(title);
    setAppBar(title, actions);
    updateData();
  }, [userId, requestedUser, setAppBar, updateData]);


  // clear tab focus listener set elsewhere
  React.useEffect(() => {
    return () => {
      window.removeEventListener('focus', onWindowFocus);
    }
  }, [onWindowFocus]);


  // refresh data when mobile modal webview closes
  React.useEffect(() => {
    const onModalWebviewClosed = () => {
      updateData();
    }

    if (window.ReactNativeWebView) {
      subscribe(APP_SUBSCRIPTION_TYPE.MODAL_WEBVIEW_CLOSED, onModalWebviewClosed);
    }

    return () => {
      unsubscribe(APP_SUBSCRIPTION_TYPE.MODAL_WEBVIEW_CLOSED, onModalWebviewClosed);
    }
  }, [subscribe, unsubscribe, updateData]);


  // if error query param is provided, show it and remove from the URL
  React.useEffect(() => {
    if (errorParam) {
      history.replace(history.location.pathname);
      enqueueSnackbar(`${errorParam}`, {
        variant: 'error',
        persist: true,
        anchorOrigin: {
          vertical: "top",
          horizontal: "center"
        }
      });
    }
  }, [errorParam, history, enqueueSnackbar]);


  const chipProps = (subscription) => {
    if (subscription.status.id === SubscriptionStatus.CANCELED && subscription.balance > 0) {
      return {
        label: 'Canceled & Overdue',
        className: 'chip-canceled-overdue'
      };
    } else if (subscription.status.id === SubscriptionStatus.CANCELED) {
      return {
        label: 'Canceled',
        className: 'chip-error'
      };
    } else if (subscription.status.id === SubscriptionStatus.OVERDUE) {
      return {
        label: 'Overdue',
        className: 'chip-warning'
      };
    } else if (subscription.status.id === SubscriptionStatus.ACTIVE) {
      return {
        label: 'Active',
        className: 'chip-ok'
      };
    }
  }


  const typeName = (subscription) => {
    if (subscription.role.id !== Types.Role.HANDLER) {
      return `Annual renewal (free)`;
    } else if (subscription.type.id === SubscriptionType.TRIAL) {
      return `Introductory 30 day trial (free)`;
    } else {
      const paymentName = subscription.paymentType.id === PaymentType.AUTOMATIC
        ? 'credit card'
        : 'invoice';
      return `${subscription.type.name} ${paymentName} payment of $${subscription.price}.`;
    }
  }


  const summary = (subscription) => {
    let text = '';
    let action = '';

    // TRIAL
    if (subscription.type.id === SubscriptionType.TRIAL) {
      // CANCELED
      if (subscription.status.id === SubscriptionStatus.CANCELED) {
        text = <>
          Your trial subscription has expired and your records are read-only.
          To restore full access to your account please upgrade to a paid subscription using the button below.
        </>;
        action = <ActionButton
          component={Link}
          to={billingEditPath}
          children='Upgrade'
          startIcon={<Icon type='billing-action' />}
        />;
      }
      // TRANSITIONING TO PAID AUTOMATIC
      else if (subscription.paymentType.id === PaymentType.AUTOMATIC && subscription.hasDefaultPayment) {
        text = <>
          When your trial period ends, your {subscription.pendingType.name.toLowerCase()} paid subscription will begin,
          and your credit card will be charged automatically.
        </>;
      }
      // TRANSITIONING TO PAID REMITTANCE
      else if (subscription.paymentType.id === PaymentType.REMITTANCE) {
        text = <>
          When your trial period ends, your {subscription.pendingType.name.toLowerCase()} paid subscription will begin.
          We will email you an invoice, and you will then have 30 days to pay before the subscription is canceled.
        </>;
      }
      // TRANSITION NOT SETUP
      else {
        text = <>
          When your trial subscription expires your records will become read-only.
          To retain full access to your account please upgrade to a paid subscription using the button below, or 
          use the billing portal to add a payment method before your subscription expires.
        </>;
        action = <ActionButton
          component={Link}
          to={billingEditPath}
          children='Upgrade'
          startIcon={<Icon type='billing-action' />}
        />;
      }
    }

    // HANDLER INVOICE (YEARLY)
    else if (subscription.paymentType.id === PaymentType.REMITTANCE) {
      // CANCELED WITH BALANCE
      if (subscription.status.id === SubscriptionStatus.CANCELED && subscription.balance > 0) {
        text = <>
          Your subscription has been canceled and your records are read-only.
          You may reactivate your subscription to restore full access to your records
          but you'll need to pay the outstanding balance on your account first.{' '}
        </>;
        action = <ActionButton
          onClick={() => openExternalActionableUrl(subscription.latestInvoicePublicUrl)}
          children='Pay Outstanding Balance'
          endIcon={<Icon type='open-in-tab' />}
        />;
      }
      // CANCELED WITH NO BALANCE
      else if (subscription.status.id === SubscriptionStatus.CANCELED && subscription.balance === 0) {
        text = <>
          Your subscription has been canceled and your records are read-only.
          Since your account has no outstanding balance you can click the Reactivate button
          below to restart your subscription and restore full access to your records.
        </>;
        action = <ActionButton
          component={Link}
          to={billingEditPath}
          children='Reactivate'
        />;
      }
      // PAYMENT DUE
      else if (subscription.balance > 0 || subscription.status.id === SubscriptionStatus.OVERDUE) {
        text = <>
          Your subscription payment is due. You have 30 days to pay after receiving your invoice.
          After that your subscription will be canceled and your account will become read-only.
          Use the button below to pay now.{' '}
        </>;
        action = <ActionButton
          onClick={() => openExternalActionableUrl(subscription.latestInvoicePublicUrl)}
          children='Pay Invoice'
          endIcon={<Icon type='open-in-tab' />}
        />;
      }
      // ACTIVE
      else {
        text = <>
          We will email you an invoice when the next subscription cycle starts.
          You will then have 30 days to pay before the subscription is canceled.
        </>;
        if (subscription.latestInvoicePublicUrl) {
          action = <ActionButtonLink
            href={subscription.latestInvoicePublicUrl}
            target={isMobile ? '_self' : '_blank'}
            children='Download Invoice/Receipt'
            endIcon={<Icon type='open-in-tab' />}
          />;
        }
      }
    }

    // HANDLER AUTOMATIC
    else if (subscription.paymentType.id === PaymentType.AUTOMATIC) {
      // CANCELED WITH BALANCE
      if (subscription.status.id === SubscriptionStatus.CANCELED && subscription.balance > 0) {
        text = <>
          Your subscription has been canceled and your records are read-only.
          Pay the outstanding balance to reactivate your subscription and restore
          full access to your records.{' '}
        </>;
        action = <ActionButton
          onClick={() => openExternalActionableUrl(subscription.latestInvoicePublicUrl)}
          children='Pay Outstanding Balance'
          endIcon={<Icon type='open-in-tab' />}
        />;
      }
      // CANCELED WITH NO BALANCE
      else if (subscription.status.id === SubscriptionStatus.CANCELED && subscription.balance === 0) {
        text = <>
          Your subscription has been canceled and your records are read-only.
          Since your account has no outstanding balance you can click the Reactivate button
          below to restart your subscription and restore full access to your records.
        </>;
        action = <ActionButton
          component={Link}
          to={billingEditPath}
          children='Reactivate'
        />;
      }
      // OVERDUE
      else if (subscription.status.id === SubscriptionStatus.OVERDUE) {
        text = <>
          Your latest credit card payment failed and your subscription is overdue.
          Your subscription will be canceled if your credit card information isn't corrected within 14 days.
          Use the billing portal to add a payment method.
        </>;
      }
      // ACTIVE
      else {
        text = <>
          Your credit card will be charged automatically when your next subscription cycle starts.
          {!handlerSubscription.hasDefaultPayment &&
            <Alert severity='warning' sx={{ marginTop: 2, marginBottom: 2 }}>
              You do not have a payment method on file. Use the billing portal to add a payment method 
              before your subscription expires.
            </Alert>
          }          
        </>;
        if (subscription.latestInvoicePublicUrl) {
          action = <ActionButtonLink
            href={subscription.latestInvoicePublicUrl}
            target={isMobile ? '_self' : '_blank'}
            children='Download Invoice/Receipt'
            endIcon={<Icon type='open-in-tab' />}
          />;
        }
      }
    }

    return (<>
      <Typography variant='subtext-large'>{text}</Typography>
      {action}
    </>)
  }


  return (
    <>
      <Container 
        data-testid='billing-view' 
        className={classes.root}
        sx={{
          '& .pt-icon-section': {
            mb: 0
          }
        }}
      >

        {/* desktop title */}
        {!isMobile &&
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <PageTitle iconName='billing' title={title} />
            <Button
              children='Refresh'
              startIcon={<Icon type='refresh' />}
              onClick={() => updateData()}
              disabled={isLoading}
            />
          </Box>}

        <FeatureBox loading={isLoading} style={{ minHeight: 80 }}>
          {subscriptionData &&
            <>
              <Card className={`${classes.card} ${classes.subCard}`}>
                <div className='title flex-hc'>
                  <Typography variant='h5'>Subscriptions</Typography>
                  <Tooltip
                    arrow
                    placement='top'
                    title={editDisabledReason}
                  >
                    <span>
                      <EditButton 
                        iconOnly={isMobile}
                        to={billingEditPath}
                        disabled={isEditDisabled}
                        className={classes.editButton}
                        children='Manage'
                      />
                    </span>
                  </Tooltip>
                </div>

                {subscriptionData.isBypassed &&
                  <Typography sx={{ mt: 2 }}>Your subscription is bypassed.</Typography>}

                {!subscriptionData.isBypassed &&
                  <div className={classes.subList}>
                    {!user.availableRoles.find(r => r.id === Types.Role.HANDLER) &&
                    <Typography sx={{ mt: 2 }}>
                      Trainer and supervisor roles don't require subscriptions. To add a handler role <MuiLink href='mailto:support@packtrack.com'>contact us</MuiLink>.
                    </Typography>}

                    {/* NO HANDLER SUB */}
                    {(hasHandlerRole && !hasHandlerSub) &&
                      <div className={classes.subscription}>
                        <IconSection iconName='subscription'>

                          <div className='title flex-hc'>
                            <Typography><b>Handler Subscription</b></Typography>
                            <Chip
                              label='No Subscription'
                              className='chip-error'
                            />
                          </div>
                          <Typography style={{ maxWidth: 700 }}>
                            You're signed up for the handler role but your account is inactive because you don't have a subscription.
                            Click the pencil icon to choose the subscription option that's right for you.
                          </Typography>
                        </IconSection>
                      </div>}

                    {/* SUB LIST */}
                    {subscriptionData.subscriptions.map(sub => (
                      <div key={sub.subscriptionId} className={classes.subscription}>
                        <IconSection iconName='subscription'>

                          {/* TITLE ROW */}
                          <div className='title flex-hc'>
                            <Typography><b>{sub.role.name} Subscription</b></Typography>
                            <Chip {...chipProps(sub)} />
                          </div>

                          {/* TYPE ROW */}
                          <Typography variant='subtext-large'>{typeName(sub)}</Typography>

                          {/* BALANCE ROW */}
                          {sub.balance > 0 &&
                          <Typography variant='subtext-large'>Balance Due: ${sub.balance}.</Typography>}

                          {/* PAID TO ROW */}
                          {(sub.role.id === Types.Role.HANDLER && sub.status.id !== SubscriptionStatus.CANCELED) &&
                            <>
                              <Typography variant='subtext-large'>
                                {sub.type.id === SubscriptionType.TRIAL ? 'Trial Expires' : 'Paid Until'}: {paidUntilDate
                                  ? <>
                                    {format(paidUntilDate, "MMM dd, yyyy")}{' '}
                                    ({formatDistance(paidUntilDate, new Date())}
                                    {paidUntilDate > new Date() ? ' from now' : ' ago'})
                                  </>
                                  : 'N/A'}
                              </Typography>
                            </>}

                          {/* SUMMARY ROW */}
                          {summary(sub)}

                          {/* PENDING CHANGES */}
                          {sub.pendingType && sub.type.id !== SubscriptionType.TRIAL &&
                            <Alert severity='info' sx={{ marginTop: 2 }}>
                              <>At the end of the current billing cycle your subscription will transition to </>
                              <strong>{' '}{PaymentTypeName[sub.paymentType.id].toLowerCase()} {SubscriptionTypeName[sub.pendingType.id].toLowerCase()} </strong>
                              <>billing.</>
                            </Alert>
                          }
                        </IconSection>
                      </div>
                    ))}
                  </div>}
              </Card>

              {/* BILLING PORTAL */}
              {(!subscriptionData.isBypassed && hasHandlerRole && (billingUser || user)?.stripeCustomerId) &&
                <Card className={`${classes.card} ${classes.billingPortalCard}`}>
                  <div className={classes.cardContent}>
                    <IconSection iconName='billing-portal'>
                      <Typography variant='subtext-large'>
                        Use the billing portal to view and download invoices and receipts, or update your payment information. 
                        Having problems? <LinkButton onClick={() => window.Intercom('showNewMessage')} children='Chat' /> with us.
                      </Typography>
                      <Alert severity='info' sx={{ my: 1 }}>PACKTRACK has a new billing system! Looking for a past invoice? Let us know in the <LinkButton onClick={() => window.Intercom('showNewMessage')} children='Support Chat' /> and we'll help you out.</Alert>
                      <Button
                        variant='contained'
                        color='primary'
                        component={!isTrackingActive ? MuiLink : undefined}
                        className={`portal-link`}
                        children={<>Billing&nbsp;Portal</>}
                        endIcon={<Icon type='open-in-tab' />}
                        onClick={() => {
                          if (isTrackingActive) {
                            alertError({
                              title: 'Action Unavailable',
                              text: `This action isn't allowed because you're currently creating a location track. To use this function, first return to the tracking interface and finish your active track.`
                            });
                          } else {
                            openBillingPortal();
                          }
                        }}
                      />
                    </IconSection>
                  </div>
                </Card>}
            </>}
        </FeatureBox>
      </Container>

      <BillingEdit
        billingUser={billingUser}
        subscriptionData={subscriptionData}
        setSubscriptionData={setSubscriptionData}
      />
    </>
  );
}